import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { MessageSquare, Bot } from "lucide-react";

export const SmartFAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [step, setStep] = useState<"idle" | "question" | "match" | "reply">("idle");

  useEffect(() => {
    if (!isInView) return;
    const t1 = setTimeout(() => setStep("question"), 800);
    const t2 = setTimeout(() => setStep("match"), 2200);
    const t3 = setTimeout(() => setStep("reply"), 3500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [isInView]);

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      <div className="container relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-lg mb-4">Clone Yourself with Smart FAQs.</h2>
            <p className="body-lg text-muted-foreground">
              Teach ClearMakes once, and it remembers forever. Define your &quot;Business Hours&quot; or &quot;Pricing&quot; answers. If a customer asks—even in different words—the AI replies automatically with your saved knowledge.
            </p>
          </motion.div>

          {/* Chat simulation Right */}
          <motion.div
            className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 overflow-hidden"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="p-4 border-b border-white/10 bg-white/5">
              <p className="text-sm font-medium">Chat simulation</p>
            </div>
            <div className="p-4 space-y-4 min-h-[200px]">
              {/* Incoming */}
              {step !== "idle" && (
                <motion.div
                  className="flex gap-2 items-start"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div className="rounded-lg rounded-tl-none bg-white/10 px-4 py-2 text-sm">
                    What time do you close?
                  </div>
                </motion.div>
              )}
              {/* AI match badge */}
              {step === "match" && (
                <motion.div
                  className="flex gap-2 items-center text-xs text-primary"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Bot className="w-4 h-4" />
                  Matches &apos;Business Hours&apos; FAQ
                </motion.div>
              )}
              {/* Outgoing reply */}
              {(step === "reply" || step === "match") && (
                <motion.div
                  className="flex gap-2 items-start justify-end"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: step === "reply" ? 0 : 0.5 }}
                >
                  <div className="rounded-lg rounded-tr-none bg-primary/20 border border-primary/30 px-4 py-2 text-sm max-w-[85%]">
                    We are open Mon-Fri until 6 PM EST.
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
